import React, { useEffect, useRef, useState } from "react";
import { Link, Redirect, useHistory, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import Icon from "./Icon";
import { Logo } from "./Header";
import { errorMessage, login, register } from "../api";
import { saveSession, useSession } from "../session";
import { money } from "../utils";
import "./AuthPage.css";

const PERKS = [
  `${money(5000)} in your wallet when you sign up`,
  "Free delivery on every order",
  "Fashion, electronics, home, sports and books",
];

const validate = (mode, { username, password, confirm }) => {
  const errors = {};
  if (!username.trim()) errors.username = "Enter your username.";
  else if (mode === "register" && username.trim().length < 6) errors.username = "Use at least 6 characters.";
  if (!password) errors.password = "Enter your password.";
  else if (mode === "register" && password.length < 6) errors.password = "Use at least 6 characters.";
  if (mode === "register" && confirm !== password) errors.confirm = "Passwords don't match.";
  return errors;
};

const AuthPage = ({ mode }) => {
  const session = useSession();
  const history = useHistory();
  const location = useLocation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const isRegister = mode === "register";

  const [form, setForm] = useState({
    username: (location.state && location.state.username) || "",
    password: "",
    confirm: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const firstField = useRef(null);

  useEffect(() => {
    setSubmitted(false);
    setForm((f) => ({ ...f, password: "", confirm: "" }));
    if (firstField.current) firstField.current.focus();
  }, [mode]);

  const from = (location.state && location.state.from) || "/";
  if (session.loggedIn) return <Redirect to={from} />;

  const errors = validate(mode, form);
  const shown = submitted ? errors : {};
  const update = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitted(true);
    if (Object.keys(errors).length) return;
    setBusy(true);
    try {
      const username = form.username.trim();
      if (isRegister) {
        await register(username, form.password);
        closeSnackbar();
        enqueueSnackbar("Account created. Log in to start shopping.", { variant: "success" });
        history.push("/login", { username });
      } else {
        const data = await login(username, form.password);
        closeSnackbar();
        enqueueSnackbar(`Welcome, ${data.username}!`, { variant: "success" });
        // Saving the session re-renders this page, which redirects to `from`.
        saveSession({ token: data.token, username: data.username, balance: data.balance });
      }
    } catch (e) {
      enqueueSnackbar(errorMessage(e), { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const fieldProps = (key) => ({
    className: `input ${shown[key] ? "is-invalid" : ""}`,
    "aria-invalid": Boolean(shown[key]),
    "aria-describedby": shown[key] ? `${key}-error` : undefined,
  });

  return (
    <div className="auth">
      <section className="auth-brand" aria-label="About QKart">
        <Logo light />
        <div className="auth-brand-body">
          <p className="auth-brand-title">India’s fastest delivery to your doorstep.</p>
          <ul className="auth-perks">
            {PERKS.map((perk) => (
              <li key={perk}>
                <span className="auth-perk-check" aria-hidden="true">
                  <Icon name="check" size={16} strokeWidth={2.6} />
                </span>
                {perk}
              </li>
            ))}
          </ul>
        </div>
        <Link to="/" className="auth-browse">
          <Icon name="arrowLeft" size={16} /> Keep browsing without an account
        </Link>
      </section>

      <main className="auth-main" id="main">
        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <nav className="auth-tabs" aria-label="Account">
            <Link to="/login" className={`auth-tab ${!isRegister ? "is-active" : ""}`} aria-current={!isRegister ? "page" : undefined}>
              Log in
            </Link>
            <Link to="/register" className={`auth-tab ${isRegister ? "is-active" : ""}`} aria-current={isRegister ? "page" : undefined}>
              Create account
            </Link>
          </nav>

          <div>
            <h1 className="auth-title">{isRegister ? "Create your account" : "Welcome back"}</h1>
            <p className="auth-sub">
              {isRegister
                ? `Sign up and get ${money(5000)} in your QKart wallet.`
                : "Log in to see your cart and check out."}
            </p>
          </div>

          <div className="field">
            <label htmlFor="username" className="field-label">
              Username
            </label>
            <input
              id="username"
              ref={firstField}
              name="username"
              autoComplete="username"
              value={form.username}
              onChange={update("username")}
              {...fieldProps("username")}
            />
            {shown.username ? (
              <span id="username-error" className="field-error">
                {shown.username}
              </span>
            ) : (
              isRegister && <span className="field-help">At least 6 characters.</span>
            )}
          </div>

          <div className="field">
            <label htmlFor="password" className="field-label">
              Password
            </label>
            <div className={`password-wrap ${shown.password ? "is-invalid" : ""}`}>
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete={isRegister ? "new-password" : "current-password"}
                value={form.password}
                onChange={update("password")}
                aria-invalid={Boolean(shown.password)}
                aria-describedby={shown.password ? "password-error" : undefined}
              />
              <button
                type="button"
                className="password-toggle"
                aria-pressed={showPassword}
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? "Hide" : "Show"}
                <span className="sr-only"> password</span>
              </button>
            </div>
            {shown.password ? (
              <span id="password-error" className="field-error">
                {shown.password}
              </span>
            ) : (
              isRegister && <span className="field-help">At least 6 characters.</span>
            )}
          </div>

          {isRegister && (
            <div className="field">
              <label htmlFor="confirm" className="field-label">
                Confirm password
              </label>
              <input
                id="confirm"
                name="confirm"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={form.confirm}
                onChange={update("confirm")}
                {...fieldProps("confirm")}
              />
              {shown.confirm && (
                <span id="confirm-error" className="field-error">
                  {shown.confirm}
                </span>
              )}
            </div>
          )}

          <button type="submit" className="btn btn-primary btn-lg" disabled={busy}>
            {busy ? "Please wait…" : isRegister ? "Create account" : "Log in to QKart"}
          </button>

          <p className="auth-switch">
            {isRegister ? (
              <>
                Already have an account? <Link to="/login">Log in</Link>
              </>
            ) : (
              <>
                New here? <Link to="/register">Create an account</Link>
              </>
            )}
          </p>
        </form>
      </main>
    </div>
  );
};

export default AuthPage;
